import { useLocation, useParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import { useState, useRef, useMemo, useEffect } from "react";
import RightSidebar from "./components/RightSidebar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion, AnimatePresence } from "framer-motion";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

/* ===============================
   HIT TEST UTILITIES
================================ */
function isPointInsidePolygon([x, y], polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    const intersect =
      yi > y !== yj > y &&
      x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function isPointInsideBbox([x, y], bbox) {
  return x >= bbox.x1 && x <= bbox.x2 && y >= bbox.y1 && y <= bbox.y2;
}

function distance(a, b) {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}
const hasZeroOrInvalidQuantities = (quantities) => {
  return Object.entries(quantities).some(
    ([_, value]) =>
      value === "" ||
      value === null ||
      isNaN(value) ||
      Number(value) <= 0
  );
};

function pointToSegmentDistance(p, v, w) {
  const l2 = (w[0] - v[0]) ** 2 + (w[1] - v[1]) ** 2;
  if (l2 === 0) return distance(p, v);

  let t =
    ((p[0] - v[0]) * (w[0] - v[0]) +
      (p[1] - v[1]) * (w[1] - v[1])) /
    l2;

  t = Math.max(0, Math.min(1, t));

  return distance(p, [
    v[0] + t * (w[0] - v[0]),
    v[1] + t * (w[1] - v[1]),
  ]);
}

function isNearPolygon(point, polygon, tolerance = 15) {
  for (let i = 0; i < polygon.length; i++) {
    const v = polygon[i];
    const w = polygon[(i + 1) % polygon.length];
    if (pointToSegmentDistance(point, v, w) < tolerance) return true;
  }
  return false;
}

function formatLabel(key) {
  return key
    .replace(/_m$/, " (m)")
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
}

/* ===============================
   MAIN COMPONENT
================================ */
const ProjectPdfViewer = () => {
  const { state } = useLocation();
  const { fileUrl } = state || {};
  const { projectId } = useParams();

  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
  const [analysisResults, setAnalysisResults] = useState({
    segmentation: [],
    detection: [],
    walls: [],
    callout: []   // 👈 ADD THIS

  });
  const [selectedObjects, setSelectedObjects] = useState([]);
  const [activeObjectId, setActiveObjectId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentAnalysisType, setCurrentAnalysisType] = useState('detection');

  const pageRef = useRef({});
  const containerRef = useRef(null);
  const detectRef = useRef(null);

  const [showPopup, setShowPopup] = useState(false);
  const [defaultQuantities, setDefaultQuantities] = useState({});
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const apiUrl = import.meta.env.VITE_API_URL;
    // const token = localStorage.getItem("token");
    // incomp change start
    fetch(`${apiUrl}/api/boq/getDefaultQuantitites/${projectId}`, {
      // incomp change end
      method: "GET",
      headers: {
        // "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
       credentials: "include",
   
    })
      .then(res => res.json())
      .then(res => {
        if (res.success) {
          setDefaultQuantities(res.data);
        }
      })
      .catch(console.error);
  }, []);

  const submitDefaultQuant = async () => {


    if (hasZeroOrInvalidQuantities(defaultQuantities)) {
      toast.error("Quantity values must be greater than 0", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL;
      // const token = localStorage.getItem("token");

      const payload = {
        drawing_id: projectId,
        defaultQuantities,
      };

      setIsExporting(true); 

      const res = await fetch(`${apiUrl}/api/boq/export_quantities`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // "Authorization": `Bearer ${token}`,
        },
         credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!data.success) {
        toast.error(data.message || "Quantities cant be Exported, Please try again Later", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
      else if (data.success) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }

      setShowPopup(false);
    } catch (err) {
      console.error("Error submitting data:", err);
      toast.error("Server error. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    }

    finally {
      setIsExporting(false); 
    }
  };

  const handleDefaultQuantity = (e) => {
    const { name, value } = e.target;
    setDefaultQuantities(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // #dynamic harsh model changes - Removed python variable as we now fetch from database instead of FastAPI
  // const python = import.meta.env.VITE_PYTHON_API_URL || "http://localhost:8000";

  // #dynamic harsh model changes - Load all analysis data from database on component mount
  useEffect(() => {
    const loadAnalysisData = async () => {
      if (!projectId) return;
      
      try {
        // #dynamic harsh model changes - Fetch all stored analysis data from database
        const apiUrl = import.meta.env.VITE_API_URL;
        // const token = localStorage.getItem("token");
        
        const response = await fetch(`${apiUrl}/api/takeoff/get-ml-analysis/${projectId}`, {
          method: "GET",
          headers: {
            // "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
          },
           credentials: "include"
        });
        
        const result = await response.json();
        console.log("Loaded analysis data from database:", result);
        
        // #dynamic harsh model changes - Set all analysis results from database
        if (result.status === "success" && result.data) {
          setAnalysisResults({
            segmentation: result.data.segmentation || [],
            detection: result.data.detection || [],
            walls: result.data.walls || [],
            callout: result.data.callout || []   

          });
        }
      } catch (error) {
        console.error("Failed to load analysis data:", error);
      }
    };
    
    loadAnalysisData();
  }, [projectId]);

  if (!fileUrl) return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 to-gray-800">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <div className="w-16 h-16 mx-auto mb-4 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-xl text-gray-300">PDF not found</p>
      </motion.div>
    </div>
  );

  const getCurrentAnalysisData = () => {
    return analysisResults[currentAnalysisType] || [];
  };

  const handleLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setTimeout(() => {
      const cw = containerRef.current?.offsetWidth;
      const ch = containerRef.current?.offsetHeight;
      const pw = pageRef.current[1]?.offsetWidth;
      const ph = pageRef.current[1]?.offsetHeight;
      if (cw && ch && pw && ph && scale === 1) {
        // Auto-fit to container with padding, considering both width and height
        const scaleW = (cw - 40) / pw;
        const scaleH = (ch - 40) / ph;
        const autoScale = Math.min(scaleW, scaleH, 1.5);
        setScale(autoScale);
      }
    }, 100);
  };

  // #dynamic harsh model changes - Modified to fetch analysis data from database instead of FastAPI
  const runSegmentationAnalysis = async () => {
    setLoading(true);
    try {
      // #dynamic harsh model changes - Fetch stored segmentation data from database
      const apiUrl = import.meta.env.VITE_API_URL;
      // const token = localStorage.getItem("token");
      
      const response = await fetch(`${apiUrl}/api/takeoff/get-ml-analysis/${projectId}`, {
        method: "GET",
        headers: {
          // "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
         credentials: "include"
      });
      
      const result = await response.json();
      
      // #dynamic harsh model changes - Use stored segmentation data from database
      if (result.status === "success" && result.data.segmentation) {
        setAnalysisResults(prev => ({ ...prev, segmentation: result.data.segmentation }));
        toast.success("Segmentation data loaded from database");
      } else {
        toast.error("No segmentation data found in database");
      }
    } catch (error) {
      console.error("Failed to load segmentation analysis:", error);
      toast.error("Failed to load segmentation analysis");
    } finally {
      setLoading(false);
    }
  };

  // #dynamic harsh model changes - Modified to fetch detection data from database instead of FastAPI
  const runDetectionAnalysis = async () => {
    setLoading(true);
    try {
      // #dynamic harsh model changes - Fetch stored detection data from database
      const apiUrl = import.meta.env.VITE_API_URL;
      // const token = localStorage.getItem("token");
      
      const response = await fetch(`${apiUrl}/api/takeoff/get-ml-analysis/${projectId}`, {
        method: "GET",
         credentials: "include",
        headers: {
          // "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        }
      });
      
      const result = await response.json();
      
      // #dynamic harsh model changes - Use stored detection data from database
      if (result.status === "success" && result.data.detection) {
        setAnalysisResults(prev => ({ ...prev, detection: result.data.detection }));
        toast.success("Detection data loaded from database");
      } else {
        toast.error("No detection data found in database");
      }
    } catch (error) {
      console.error("Failed to load detection analysis:", error);
      toast.error("Failed to load detection analysis");
    } finally {
      setLoading(false);
    }
  };

  // #dynamic harsh model changes - Modified to fetch wall data from database instead of FastAPI
  const runWallAnalysis = async () => {
    setLoading(true);
    try {
      // #dynamic harsh model changes - Fetch stored wall data from database
      const apiUrl = import.meta.env.VITE_API_URL;
      // const token = localStorage.getItem("token");
      
      const response = await fetch(`${apiUrl}/api/takeoff/get-ml-analysis/${projectId}`, {
        method: "GET",
         credentials: "include",
        headers: {
          // "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        }
      });
      
      const result = await response.json();
      
      // #dynamic harsh model changes - Use stored wall data from database
      if (result.status === "success" && result.data.walls) {
        setAnalysisResults(prev => ({ ...prev, walls: result.data.walls }));
        toast.success("Wall data loaded from database");
      } else {
        toast.error("No wall data found in database");
      }
    } catch (error) {
      console.error("Failed to load wall analysis:", error);
      toast.error("Failed to load wall analysis");
    } finally {
      setLoading(false);
    }
  };


  const runCalloutAnalysis = async () => {
  setLoading(true);
  try {
    const apiUrl = import.meta.env.VITE_API_URL;
    // const token = localStorage.getItem("token");

    const response = await fetch(`${apiUrl}/api/takeoff/get-ml-analysis/${projectId}`, {
      method: "GET",
       credentials: "include",
      headers: {
        // "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });

    const result = await response.json();

    if (result.status === "success" && result.data.callout) {
      setAnalysisResults(prev => ({
        ...prev,
        callout: result.data.callout
      }));
      toast.success("Callout data loaded from database");
    } else {
      toast.error("No callout data found in database");
    }
  } catch (error) {
    console.error("Failed to load callout analysis:", error);
    toast.error("Failed to load callout analysis");
  } finally {
    setLoading(false);
  }
};


  const handleRunAnalysis = (analysisType) => {
    console.log("Running analysis for type:", analysisType);
    switch (analysisType) {
      case 'segmentation':
        runSegmentationAnalysis();
        break;
      case 'detection':
        runDetectionAnalysis();
        break;
      case 'walls':
        runWallAnalysis();
        break;
      case 'callout':
        runCalloutAnalysis();
        break;  
      default:
        console.warn('Unknown analysis type:', analysisType);
    }
  };

  const handlePageClick = (e, page) => {
    if (isPanning) return;
    
    const rect = pageRef.current[page].getBoundingClientRect();
    const x = Math.round((e.clientX - rect.left - panOffset.x) / scale);
    const y = Math.round((e.clientY - rect.top - panOffset.y) / scale);

    if (detectRef.current) clearInterval(detectRef.current);
    let tolerance = 2;

    const currentData = getCurrentAnalysisData();

    detectRef.current = setInterval(() => {
      const matched = currentData.find((item) => {
        if (item.bbox && isPointInsideBbox([x, y], item.bbox)) {
          return true;
        }
        if (item.polygon && (
          isPointInsidePolygon([x, y], item.polygon) ||
          isNearPolygon([x, y], item.polygon, tolerance)
        )) {
          return true;
        }
        return false;
      });

      if (matched) {
        setActiveObjectId(matched.object_id);
        setSelectedObjects([matched]);
        clearInterval(detectRef.current);
        detectRef.current = null;
      } else if (tolerance > 40) {
        clearInterval(detectRef.current);
        detectRef.current = null;
      }
      tolerance += 3;
    }, 40);
  };

  const handleMouseDown = (e) => {
    if (e.button === 1 || e.ctrlKey) { // Middle mouse or Ctrl+click for panning
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning) {
      const deltaX = e.clientX - lastPanPoint.x;
      const deltaY = e.clientY - lastPanPoint.y;
      setPanOffset(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      setLastPanPoint({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handleWheel = (e) => {
    if (e.ctrlKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const newScale = Math.max(0.1, Math.min(5, scale + delta));
      
      if (newScale !== scale) {
        // Get mouse position relative to the container
        const rect = containerRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        // Calculate the zoom center point
        const zoomPointX = (mouseX - panOffset.x) / scale;
        const zoomPointY = (mouseY - panOffset.y) / scale;
        
        // Update scale
        setScale(newScale);
        
        // Adjust pan offset to keep zoom centered on mouse position
        setPanOffset({
          x: mouseX - zoomPointX * newScale,
          y: mouseY - zoomPointY * newScale
        });
      }
    }
  };

  const memoizedFile = useMemo(() => ({ url: fileUrl }), [fileUrl]);

  return (
    <div className="h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-2 lg:p-4">
      <div className="flex gap-4 h-full">
        {/* ===== MAIN CONTENT ===== */}
        <div className="flex-1 flex flex-col gap-2 lg:gap-4 min-h-0">
          {/* TOP CONTROLS */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-gray-800/50 backdrop-blur-sm rounded-xl p-3 lg:p-4 border border-gray-700 gap-3 sm:gap-0 flex-shrink-0">
            <div className="flex flex-wrap items-center gap-2 lg:gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowPopup(true)}
                className="px-3 lg:px-6 py-2 lg:py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-emerald-500/30 transition-all duration-300 flex items-center gap-2 group text-sm lg:text-base"
              >
                <svg className="w-5 h-5 group-hover:rotate-12 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Export Quantities
              </motion.button>

              <div className="flex items-center gap-1 bg-gray-900/50 rounded-lg p-1">
                <button
                  onClick={() => setScale(s => Math.max(0.1, s - 0.1))}
                  className="px-2 lg:px-3 py-1 lg:py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors flex items-center gap-1 text-sm"
                >
                  <svg className="w-3 h-3 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                  <span className="hidden sm:inline">Zoom Out</span>
                </button>
                <span className="px-2 lg:px-3 py-1 lg:py-2 text-xs lg:text-sm font-mono text-blue-400 bg-gray-800 rounded">
                  {scale.toFixed(1)}x
                </span>
                <button
                  onClick={() => setScale(s => Math.min(5, s + 0.1))}
                  className="px-2 lg:px-3 py-1 lg:py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors flex items-center gap-1 text-sm"
                >
                  <svg className="w-3 h-3 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="hidden sm:inline">Zoom In</span>
                </button>
                <button
                  onClick={() => {
                    setPanOffset({ x: 0, y: 0 });
                    const cw = containerRef.current?.offsetWidth;
                    const ch = containerRef.current?.offsetHeight;
                    const pw = pageRef.current[1]?.offsetWidth;
                    const ph = pageRef.current[1]?.offsetHeight;
                    if (cw && ch && pw && ph) {
                      const scaleW = (cw - 40) / pw;
                      const scaleH = (ch - 40) / ph;
                      const autoScale = Math.min(scaleW, scaleH, 1.5);
                      setScale(autoScale);
                    }
                  }}
                  className="px-2 lg:px-3 py-1 lg:py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors flex items-center gap-1 text-sm"
                >
                  <svg className="w-3 h-3 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                  <span className="hidden sm:inline">Fit to Screen</span>
                </button>
              </div>
            </div>

            {/* <div className="text-right">
              <p className="text-xs lg:text-sm text-gray-400">Controls</p>
              <p className="text-xs text-gray-500">Ctrl+Scroll: Zoom | Ctrl+Drag: Pan | Click: Select</p>
            </div> */}
          </div>

          {/* PDF VIEWER */}
          <div 
            ref={containerRef} 
            className="flex-1 bg-gray-900/50 rounded-xl border border-gray-700 overflow-auto relative"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
            style={{ cursor: isPanning ? 'grabbing' : 'grab' }}
          >
            <div 
              className="inline-block p-4"
              style={{ 
                transform: `translate(${panOffset.x}px, ${panOffset.y}px)`,
                cursor: isPanning ? 'grabbing' : 'crosshair',
                minWidth: 'max-content',
                minHeight: 'max-content'
              }}
            >
              <Document file={memoizedFile} onLoadSuccess={handleLoadSuccess}>
                {Array.from({ length: numPages }, (_, i) => {
                  const pageNumber = i + 1;
                  const currentData = getCurrentAnalysisData();

                  return (
                    <div
                      key={pageNumber}
                      ref={(el) => (pageRef.current[pageNumber] = el)}
                      onClick={(e) => handlePageClick(e, pageNumber)}
                      className="relative block hover:shadow-2xl transition-shadow duration-300"
                      style={{ marginBottom: pageNumber < numPages ? '20px' : '0' }}
                    >
                    <Page
                      pageNumber={pageNumber}
                      scale={scale}
                      className="shadow-lg"
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                    />

                    <svg
                      className="absolute inset-0 pointer-events-none"
                      viewBox={`0 0 ${pageRef.current[pageNumber]?.offsetWidth / scale || 1000} ${pageRef.current[pageNumber]?.offsetHeight / scale || 1000}`}
                    >
                      {currentData.map((item) => {
                      

                        const isActive = activeObjectId === item.object_id;

                        if (item.polygon && (item.class === 'area' || item.class === 'room' || item.class === 'wall' || item.class === 'callout_1')) {
                          const color = item.class === 'area' || item.class === 'room' ? '#10B981' : '#F97316';
                          return (
                            <motion.polygon
                              key={item.object_id}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: isActive ? 0.8 : 0.6 }}
                              transition={{ duration: 0.3 }}
                              points={item.polygon.map(p => p.join(",")).join(" ")}
                              fill={isActive ? "rgba(239, 68, 68, 0.7)" : `${color}80`}
                              stroke={isActive ? "#EF4444" : color}
                              strokeWidth={isActive ? 4 : 3}
                              className="transition-all duration-300"
                            />
                          );
                        }

                        // if (item.bbox && (item.class === 'door' || item.class === 'window' || item.class === 'callout')) {
                        //   const { x1, y1, width, height } = item.bbox;
                        //   const color = item.class === 'door' ? '#3B82F6' : '#06B6D4';
                        //   return (
                        //     <motion.rect
                        //       key={item.object_id}
                        //       initial={{ opacity: 0 }}
                        //       animate={{ opacity: isActive ? 0.8 : 0.6 }}
                        //       transition={{ duration: 0.3 }}
                        //       x={x1}
                        //       y={y1}
                        //       width={width}
                        //       height={height}
                        //       fill={isActive ? "rgba(239, 68, 68, 0.7)" : `${color}80`}
                        //       stroke={isActive ? "#EF4444" : color}
                        //       strokeWidth={isActive ? 4 : 3}
                        //       rx="2"
                        //       className="transition-all duration-300"
                        //     />
                        //   );
                        // }

                        if (item.bbox && ['door','window','callout_1'].includes(item.class)) {

                          const { x1, y1, width, height } = item.bbox;

                          let color = '#06B6D4'; // default

                          if (item.class === 'door') color = '#3B82F6';
                          if (item.class === 'window') color = '#06B6D4';
                          if (item.class === 'callout_1') color = '#E11D48'; //  RED-PINK for callout

                          return (
                            <motion.rect
                              key={item.object_id}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: isActive ? 0.9 : 0.7 }}
                              transition={{ duration: 0.3 }}
                              x={x1}
                              y={y1}
                              width={width}
                              height={height}
                              fill={isActive ? "rgba(239, 68, 68, 0.7)" : `${color}80`}
                              stroke={isActive ? "#EF4444" : color}
                              strokeWidth={isActive ? 4 : 3}
                              rx="3"
                            />
                          );
                        }

                        return null;
                      })}
                    </svg>
                    </div>
                  );
                })}
              </Document>
            </div>
          </div>
        </div>

        {/* ===== RIGHT SIDEBAR ===== */}
        <motion.div
          initial={{ x: 20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="w-80 flex-shrink-0 bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl shadow-2xl border border-gray-700 p-4 overflow-y-auto max-h-full"
        >
          {/* Analysis Controls */}
          <RightSidebar
            projectId={projectId}
            onAnalysisTypeChange={setCurrentAnalysisType}
            currentAnalysisType={currentAnalysisType}
            onRunAnalysis={handleRunAnalysis}
            isLoading={loading}
          />
          
          {/* Selected Objects */}
          <div className="mt-6 border-t border-gray-700 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                Selected Objects
              </h4>
              {selectedObjects.length > 0 && (
                <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full">
                  {selectedObjects.length}
                </span>
              )}
            </div>

            {loading && (
              <div className="flex items-center justify-center p-4">
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="ml-2 text-gray-300">Loading analysis...</span>
              </div>
            )}

            {!loading && selectedObjects.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center p-6"
              >
                <div className="w-12 h-12 mx-auto mb-3 text-gray-500">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                </div>
                <p className="text-gray-400">Click on objects to select</p>
                <p className="text-xs text-gray-500 mt-1">AI will detect and highlight</p>
              </motion.div>
            )}

            <AnimatePresence>
              {selectedObjects.map((obj) => (
                <motion.div
                  key={obj.object_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="relative mb-3 group"
                >
                  <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg p-4 border border-gray-700 shadow-lg hover:shadow-blue-500/10 transition-all duration-300">
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => {
                          setSelectedObjects((prev) =>
                            prev.filter((o) => o.object_id !== obj.object_id)
                          );
                          if (activeObjectId === obj.object_id) {
                            setActiveObjectId(null);
                          }
                        }}
                        className="w-6 h-6 flex items-center justify-center bg-red-500/20 text-red-400 rounded-full hover:bg-red-500/30 transition-colors"
                      >
                        ✕
                      </button>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${obj.class === 'room' ? 'bg-green-500/20' : obj.class === 'wall' ? 'bg-orange-500/20' : 'bg-blue-500/20'}`}>
                        <span className={`text-sm font-semibold ${obj.class === 'room' ? 'text-green-400' : obj.class === 'wall' ? 'text-orange-400' : 'text-blue-400'}`}>
                          {obj.name.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <h5 className="font-semibold text-white">{obj.name}</h5>
                          <span className={`text-xs px-2 py-1 rounded-full ${obj.confidence >= 0.7 ? 'bg-green-500/20 text-green-300' : obj.confidence >= 0.5 ? 'bg-yellow-500/20 text-yellow-300' : 'bg-red-500/20 text-red-300'}`}>
                            {(obj.confidence * 100).toFixed(0)}%
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mb-2">ID: {obj.object_id}</p>

                        {/* Measurements Display for Different Object Types */}
                        {obj.measurements && (
                          <div className="mt-3 space-y-2">
                            {/* Room/Area Measurements */}
                            {(obj.class === 'room' || obj.class === 'area') && (
                              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                                <p className="text-xs font-medium text-green-400 mb-2">Room Measurements</p>
                                <div className="grid grid-cols-2 gap-2">
                                  {obj.measurements.area_square_meters && (
                                    <div>
                                      <p className="text-xs text-gray-400">Area</p>
                                      <p className="text-sm font-semibold text-green-300">{obj.measurements.area_square_meters.toFixed(2)} m²</p>
                                    </div>
                                  )}
                                  {obj.measurements.perimeter_meters && (
                                    <div>
                                      <p className="text-xs text-gray-400">Perimeter</p>
                                      <p className="text-sm font-semibold text-green-300">{obj.measurements.perimeter_meters.toFixed(2)} m</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Wall Measurements */}
                            {obj.class === 'wall' && (
                              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3">
                                <p className="text-xs font-medium text-orange-400 mb-2">Wall Measurements</p>
                                <div className="grid grid-cols-2 gap-2">
                                  {obj.measurements.wall_length_meters && (
                                    <div>
                                      <p className="text-xs text-gray-400">Length</p>
                                      <p className="text-sm font-semibold text-orange-300">{obj.measurements.wall_length_meters.toFixed(2)} m</p>
                                    </div>
                                  )}
                                  {obj.measurements.wall_thickness_meters && (
                                    <div>
                                      <p className="text-xs text-gray-400">Thickness</p>
                                      <p className="text-sm font-semibold text-orange-300">{obj.measurements.wall_thickness_meters.toFixed(2)} m</p>
                                    </div>
                                  )}
                                  {obj.measurements.area_square_meters && (
                                    <div className="col-span-2">
                                      <p className="text-xs text-gray-400">Wall Area</p>
                                      <p className="text-sm font-semibold text-orange-300">{obj.measurements.area_square_meters.toFixed(2)} m²</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Door/Window Measurements */}
                            {(obj.class === 'door' || obj.class === 'window') && obj.bbox && (
                              <div className={`${obj.class === 'door' ? 'bg-blue-500/10 border-blue-500/20' : 'bg-cyan-500/10 border-cyan-500/20'} border rounded-lg p-3`}>
                                <p className={`text-xs font-medium ${obj.class === 'door' ? 'text-blue-400' : 'text-cyan-400'} mb-2`}>
                                  {obj.class === 'door' ? 'Door' : 'Window'} Dimensions
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <p className="text-xs text-gray-400">Width</p>
                                    <p className={`text-sm font-semibold ${obj.class === 'door' ? 'text-blue-300' : 'text-cyan-300'}`}>
                                      {(obj.bbox.width / 100).toFixed(2)} m
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-400">Length</p>
                                    <p className={`text-sm font-semibold ${obj.class === 'door' ? 'text-blue-300' : 'text-cyan-300'}`}>
                                      {(obj.bbox.height / 100).toFixed(2)} m
                                    </p>
                                  </div>
                                  <div className="col-span-2">
                                    <p className="text-xs text-gray-400">Area</p>
                                    <p className={`text-sm font-semibold ${obj.class === 'door' ? 'text-blue-300' : 'text-cyan-300'}`}>
                                      {((obj.bbox.width * obj.bbox.height) / 10000).toFixed(2)} m²
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Fallback for area_m2 if measurements not available */}
                        {!obj.measurements && obj.area_m2 && (
                          <div className="mt-2 p-2 bg-gray-800/50 rounded">
                            <p className="text-xs text-gray-400">Area</p>
                            <p className="text-sm font-semibold text-green-400">{obj.area_m2.toFixed(2)} m²</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      {/* ===== MODAL POPUP ===== */}
      <AnimatePresence>
        {showPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowPopup(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25 }}
              className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-2xl shadow-2xl border border-gray-700 w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                    <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                   Default Drawing settings
                  </h3>
                  <button
                    onClick={() => setShowPopup(false)}
                    className="text-gray-400 hover:text-white transition-colors p-1"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                  {Object.entries(defaultQuantities).map(([key, value]) => (
                    <motion.div
                      key={key}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: Object.keys(defaultQuantities).indexOf(key) * 0.05 }}
                    >
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {formatLabel(key)}
                      </label>
                      <div className="relative">
                      <input
                        type="number"
                        min="1"
                        step="any"
                        name={key}
                        value={value}
                        onChange={handleDefaultQuantity}
                        className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-4 py-3 text-white"
                        placeholder="Enter value"
                      />
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                          units
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="flex gap-3 pt-6 border-t border-gray-700">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowPopup(false)}
                    className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium rounded-lg transition-colors"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
  whileHover={!isExporting ? { scale: 1.02 } : {}}
  whileTap={!isExporting ? { scale: 0.98 } : {}}
  onClick={submitDefaultQuant}
  disabled={isExporting}
  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 
             hover:from-blue-700 hover:to-blue-800 
             text-white font-medium rounded-lg shadow-lg 
             transition-all duration-300 
             disabled:opacity-50 disabled:cursor-not-allowed
             flex items-center justify-center gap-2"
>
  {isExporting ? (
    <>
      {/* Spinner */}
      <svg
        className="animate-spin h-5 w-5 text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v8H4z"
        />
      </svg>

      Exporting...
    </>
  ) : (
    "Export Quantities"
  )}
</motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
};

export default ProjectPdfViewer;